import type { UIMessage } from 'ai'
import {
  type ChannelMessageSource,
  getChannelThreadMessages,
  isChannelMessageSource,
} from './channels/message-source'

export type SelectedEmailMetadata = {
  emailId: string
  title: string
  imagePngUrl?: string | null
  /**
   * Exact immutable row audited by an application-authored repair handoff.
   * This stays in the structured data part and is never rendered in the
   * selection footer. Normal composer attachments omit it.
   */
  emailVersionId?: string
  /**
   * Optional pinned email version. When set, the user was previewing /
   * referencing that historical version of the email when they submitted
   * the message. The orchestrator route forwards this through to the
   * email subagent so reference + edit lookups resolve to the exact
   * version the user was looking at instead of the current latest row.
   *
   * Omit (or set to `'latest'`) for default latest-version behavior so
   * existing wire formats stay byte-identical.
   */
  version?: number | 'latest'
  /**
   * Owning automation id when this design is currently referenced by a
   * `sendEmail` node. Surfaced in the per-message selection footer so
   * the orchestrator stays on the automations canvas and routes via
   * `routeToEmailAgent({forAutomationId})` for edits. A back-pointer for
   * canvas/cascade routing — NOT a "type": the design is shared and can
   * still be sent as a standalone campaign. Resolved server-side from
   * `emails.automationId` on the loaded row by the orchestrator route —
   * clients populate it when they have the id on hand (sendEmail node
   * toolbar) but it is NOT required to be persisted on the client-side
   * data part.
   */
  automationId?: string
}

export type SelectedAutomationMetadata = {
  automationId: string
  title: string
  lifecycleStatus?: 'draft' | 'published'
  /**
   * Exact automation row id the user was viewing on the canvas when
   * they attached this automation to chat. Set by the canvas toolbar
   * via `automation.viewedVersionId ?? viewedRow.automationVersionId`.
   * The orchestrator route uses it to load THAT specific version's
   * graph (and its `sendEmail.config.emailVersionId` pins) instead
   * of defaulting to `version === 'latest'`. Mirrors the email
   * metadata's `version` field.
   */
  automationVersionId?: string
  /**
   * Human-readable version pin: numeric version number or `'latest'`.
   * Resolved server-side by the orchestrator route from the picked
   * automation row's `version` column and rendered in the SELECTED
   * AUTOMATIONS prompt block as `automationVersion=N` /
   * `automationVersion="latest"`. Pairs with `automationVersionId`
   * — the id is canonical, the number is for prompt readability +
   * the non-latest footnote signal. Clients DO NOT persist this on
   * submit (the route fills it in via Convex resolution).
   */
  automationVersion?: number | 'latest'
  /**
   * Optional rich fields populated by the orchestrator route's
   * `enrichLatestUserMessageWithLiveSelection` for the LATEST user
   * message ONLY. Convex-hydrated from the automation row +
   * `summarizeAutomationGraph`; not written by the client at submit
   * time. Persisted shape from `buildSelectedAutomationsDataPart`
   * keeps only the required + `lifecycleStatus` fields so historical
   * turns stay light.
   */
  description?: string
  triggerEventId?: string
  triggerEventName?: string
  triggerProvider?: string
  emailIds?: ReadonlyArray<string>
  nodeCounts?: {
    trigger: number
    sendEmail: number
    wait: number
    filter: number
    split: number
  }
}

export type ChatMessageMetadata = {
  userId?: string
  /** The actual invoking message; quoted teammates never replace its author. */
  channelSource?: ChannelMessageSource
  selectedEmails?: Array<SelectedEmailMetadata>
  selectedAutomations?: Array<SelectedAutomationMetadata>
  /**
   * Marks a synthetic assistant message persisted when a run FAILED
   * (see `buildAssistantRunFailedMessage`). Error-marked messages render
   * as an inline error bubble in the chat thread and are excluded from
   * model-bound messages in `sanitizeMessagesForModel` so they never
   * pollute later turns' context.
   */
  error?: boolean
  errorKind?: 'run_failed'
  /**
   * Marks an assistant message whose run the USER stopped mid-response.
   *
   * Unlike `error` this does NOT hide the message from the model: the
   * partial reply is real work and the next turn needs it to continue
   * coherently. What it changes is framing — the UI labels the message
   * "Stopped" and offers Continue, and the model-bound copy gets an
   * explicit interruption marker appended
   * (`appendStoppedTurnMarkerForModel`) so the model knows the text
   * trails off because it was cut, not because it finished.
   */
  stopped?: boolean
  stopReason?: 'user_stopped'
}

export type WithChatMessageMetadata<T extends UIMessage = UIMessage> = T & {
  metadata?: ChatMessageMetadata
}

/**
 * Default user-facing copy for the run-failed bubble. Deliberately
 * generic — raw server error text never reaches the transcript (detail
 * stays in server logs).
 */
export const ASSISTANT_RUN_FAILED_MESSAGE_TEXT =
  'Something went wrong while generating this response. Try again, or rephrase your message.'

/**
 * Client-visible copy for a tool call whose ARGUMENTS the tool's schema
 * rejected (`tool-input-error`) and that the loop later retried. It must
 * not read like the run failed, and it carries no raw validation detail
 * (that stays in logs). The never-retried twin is
 * `TOOL_INPUT_UNRECOVERED_MESSAGE_TEXT`.
 */
export const TOOL_INPUT_REJECTED_MESSAGE_TEXT =
  'The step’s arguments were rejected, and the assistant re-fired it.'

/**
 * The same rejection when the loop ended without recovering: the step never
 * ran. Rendered in place of the persisted re-fired copy once the stream has
 * settled with no later completed tool call of any tool in the message.
 */
export const TOOL_INPUT_UNRECOVERED_MESSAGE_TEXT =
  'The step’s arguments were rejected, and the assistant did not retry it.'

/**
 * True for assistant messages persisted purely to surface a failed run
 * (the inline error bubble). Pure and shape-tolerant so it can run on
 * raw persisted rows, model-bound copies, and client UI messages alike.
 */
export function isChatErrorAssistantMessage(message: {
  role?: unknown
  metadata?: unknown
}): boolean {
  if (message?.role !== 'assistant') {
    return false
  }
  const metadata = message.metadata as ChatMessageMetadata | undefined | null
  return metadata?.error === true
}

/**
 * Build the synthetic assistant message persisted when a run fails.
 * The single done-state text part matters: empty-parts messages are
 * dropped by `filterRenderableMessages` and rejected by
 * `upsertAssistantMessage`. Note the client's completed-tail detection
 * (`hasCompletedAssistantTail`) deliberately EXCLUDES error bubbles —
 * a trailing bubble must not read as "this turn was answered" while a
 * retry is pending.
 */
export function buildAssistantRunFailedMessage(args?: {
  id?: string
  text?: string
}): WithChatMessageMetadata {
  return {
    id: args?.id ?? crypto.randomUUID(),
    role: 'assistant',
    metadata: { error: true, errorKind: 'run_failed' },
    parts: [
      {
        type: 'text',
        state: 'done',
        text: args?.text ?? ASSISTANT_RUN_FAILED_MESSAGE_TEXT,
      },
    ],
  } as WithChatMessageMetadata
}

/**
 * Prompt sent by the Continue affordance on a stopped turn. Pairs with
 * the marker the model-bound transcript carries
 * (`STOPPED_TURN_MARKER_TEXT`), which tells the model to resume from
 * the cut-off point rather than restart.
 */
export const CONTINUE_STOPPED_TURN_PROMPT = 'Continue from where you stopped.'

/**
 * True for an assistant message the user stopped mid-response.
 * Shape-tolerant so it runs on raw persisted rows, model-bound copies,
 * and client UI messages alike (same contract as
 * `isChatErrorAssistantMessage`).
 */
export function isChatStoppedAssistantMessage(message: {
  role?: unknown
  metadata?: unknown
}): boolean {
  if (message?.role !== 'assistant') {
    return false
  }
  const metadata = message.metadata as ChatMessageMetadata | undefined | null
  return metadata?.stopped === true
}

/**
 * Stamp the user-stopped marker onto an assistant message, preserving
 * every other metadata field. Idempotent, and a no-op for non-assistant
 * messages — both matter because the stop-finalize path can run twice
 * (the run's own terminal write, then the scheduled force-finalize) and
 * may be handed a trailing USER message when the run was stopped before
 * it produced anything.
 */
export function markChatMessageStopped<T extends { role?: unknown }>(
  message: T
): T {
  if (message?.role !== 'assistant') {
    return message
  }
  const metadata = (message as { metadata?: ChatMessageMetadata }).metadata
  const nextMetadata =
    metadata?.stopped === true
      ? metadata
      : {
          ...(metadata ?? {}),
          stopped: true,
          stopReason: 'user_stopped' as const,
        }
  const parts = (message as { parts?: unknown }).parts
  const nextParts = stampStoppedToolOutputs(parts)
  if (nextMetadata === metadata && nextParts === parts) {
    return message
  }
  return { ...message, metadata: nextMetadata, parts: nextParts } as T
}

/**
 * Flag every settled tool part on a stopped turn as user-stopped.
 *
 * The sub-agent generator sets `stoppedByUser` on the value it yields at
 * abort time, but that yield RACES the parent stream closing and reliably
 * loses: live runs persist the earlier preliminary output instead, so the
 * flag never reached the transcript and `buildEmailToolModelOutput` kept
 * routing stopped runs to the no-op block — whose copy
 * (`Do-not-retry: true (ask the user for a concrete instruction instead)`)
 * is what made Continue ask the user to restate the request.
 *
 * Stamping here cannot race: it runs wherever a message is MARKED stopped,
 * which is after the run has already settled — the route's `onFinish`, the
 * Convex stop-time promotion, and the +45s backstop all funnel through
 * this one function. `toModelOutput` re-runs against the persisted part on
 * every later turn, so the stamp is what the model actually reads.
 */
function stampStoppedToolOutputs(parts: unknown): unknown {
  if (!Array.isArray(parts)) {
    return parts
  }
  let hasMutated = false
  const next = parts.map((part) => {
    const candidate = part as {
      type?: unknown
      output?: Record<string, unknown>
    }
    const type = typeof candidate?.type === 'string' ? candidate.type : ''
    if (!type.startsWith('tool-') && type !== 'dynamic-tool') {
      return part
    }
    // Only a part that actually produced output — an interrupted call with
    // none is already terminalized to `output-error` elsewhere, and the
    // model reads that as interrupted without needing a flag.
    if (!candidate.output || typeof candidate.output !== 'object') {
      return part
    }
    if (candidate.output.stoppedByUser === true) {
      return part
    }
    // Only calls still IN FLIGHT at the stop. A stopped turn routinely
    // contains tool calls that already FINISHED — the N+1 fan-out settles
    // several subagents before the user presses stop — and flagging those
    // tells the model completed work was interrupted, so it re-issues them
    // and duplicates the artifacts. The persisted output of an in-flight
    // call is its PRELIMINARY yield (`status: 'streaming'`); a finished one
    // carries `status: 'complete'`. Note the tool's own abort-time yield
    // never lands here — the AI SDK discards every value read after
    // `abortSignal.aborted` (`pull()` in ai/dist/index.mjs) — which is
    // exactly why this stamping exists and why `'streaming'` is still the
    // value on disk for an interrupted call.
    if (candidate.output.status !== 'streaming') {
      return part
    }
    hasMutated = true
    return {
      ...candidate,
      output: { ...candidate.output, stoppedByUser: true },
    }
  })
  return hasMutated ? next : parts
}

/**
 * Type literal for the AI SDK `data-*` UI part that carries canvas
 * selection alongside a user message. Persisted as an ordinary message
 * part so the orchestrator can recover historical selection context on
 * follow-up turns even after the canvas auto-clears the live selection.
 */
// ─── Brand-extraction input data part ────────────────────────────────────────

export const BRAND_EXTRACTION_INPUT_DATA_PART_TYPE =
  'data-brand-extraction-input' as const

/**
 * The first message of a brand-EXTRACTION chat. Carries the structured
 * inputs (domain + crawl blacklist) so the chat UI renders dedicated chips
 * (domain w/ favicon, exclude pills) instead of a raw text blob. The model
 * receives the same info as text via `convertBrandExtractionInputToModelText`.
 * Uploaded files travel as their own `file` parts alongside this part.
 */
export type BrandExtractionInputDataPart = {
  type: typeof BRAND_EXTRACTION_INPUT_DATA_PART_TYPE
  id?: string
  data: {
    domain: string
    faviconUrl?: string
    excludePaths?: Array<string>
    excludeSubdomains?: Array<string>
    /** Free-text user guidance — AUTHORITATIVE, treated like an uploaded brand guideline (overrides conflicting domain signals). */
    instructions?: string
    /** Specific page URLs the user wants read/prioritized (beyond the auto-crawl). */
    includePaths?: Array<string>
  }
}

export function buildBrandExtractionInputDataPart(input: {
  domain: string
  faviconUrl?: string
  excludePaths?: ReadonlyArray<string>
  excludeSubdomains?: ReadonlyArray<string>
  instructions?: string
  includePaths?: ReadonlyArray<string>
}): BrandExtractionInputDataPart {
  return {
    type: BRAND_EXTRACTION_INPUT_DATA_PART_TYPE,
    data: {
      domain: input.domain,
      ...(input.faviconUrl ? { faviconUrl: input.faviconUrl } : {}),
      ...(input.excludePaths?.length
        ? { excludePaths: [...input.excludePaths] }
        : {}),
      ...(input.excludeSubdomains?.length
        ? { excludeSubdomains: [...input.excludeSubdomains] }
        : {}),
      ...(input.instructions?.trim()
        ? { instructions: input.instructions.trim() }
        : {}),
      ...(input.includePaths?.length
        ? { includePaths: [...input.includePaths] }
        : {}),
    },
  }
}

export function isBrandExtractionInputDataPart(
  part: unknown
): part is BrandExtractionInputDataPart {
  if (!part || typeof part !== 'object') {
    return false
  }
  const candidate = part as { type?: unknown; data?: unknown }
  if (candidate.type !== BRAND_EXTRACTION_INPUT_DATA_PART_TYPE) {
    return false
  }
  if (!candidate.data || typeof candidate.data !== 'object') {
    return false
  }
  const data = candidate.data as { domain?: unknown }
  return typeof data.domain === 'string' && data.domain.length > 0
}

// ─── Remix source: the template or flow a turn rebuilds ──────────────────

export const REMIX_SOURCE_DATA_PART_TYPE = 'data-remix-source' as const

export type RemixSourceKind = 'template' | 'flow'

/**
 * The public template or flow template a user turn remixes — attached
 * beside the selection parts when a remix chip is on the composer. The
 * user turn renders it as a card (`UserRemixSource`), and the model reads
 * it as one line, so the conversation stays self-explanatory on later
 * turns. Small and flat: strings and a bounded list of card URLs.
 */
export type RemixSourceMetadata = {
  kind: RemixSourceKind
  /** The template's public reference (`pt1_…`) or the flow's slug. */
  id: string
  title: string
  /** Where the source lives in the app (the template or flow page). */
  href: string
  /** "By Hypermatic" / "5 emails in 14 days". */
  subtitle?: string
  imagePngUrl?: string
  /** A flow's first step cards, in order — at most `REMIX_SOURCE_THUMBNAIL_CAP`. */
  thumbnails?: Array<string>
}

export type RemixSourceDataPart = {
  type: typeof REMIX_SOURCE_DATA_PART_TYPE
  id?: string
  data: RemixSourceMetadata
}

export const REMIX_SOURCE_THUMBNAIL_CAP = 3

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

export function isValidRemixSourceMetadata(
  value: unknown
): value is RemixSourceMetadata {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as Record<string, unknown>
  if (candidate.kind !== 'template' && candidate.kind !== 'flow') {
    return false
  }
  if (
    !(
      isNonEmptyString(candidate.id) &&
      isNonEmptyString(candidate.title) &&
      isNonEmptyString(candidate.href)
    )
  ) {
    return false
  }
  for (const key of ['subtitle', 'imagePngUrl'] as const) {
    if (candidate[key] !== undefined && !isNonEmptyString(candidate[key])) {
      return false
    }
  }
  if (
    candidate.thumbnails !== undefined &&
    !(
      Array.isArray(candidate.thumbnails) &&
      candidate.thumbnails.every(isNonEmptyString)
    )
  ) {
    return false
  }
  return true
}

/** The persisted shape: validated, only the fields set, the card list capped. */
export function buildRemixSourceDataPart(
  source: RemixSourceMetadata
): RemixSourceDataPart | null {
  if (!isValidRemixSourceMetadata(source)) {
    return null
  }
  const thumbnails = (source.thumbnails ?? []).slice(
    0,
    REMIX_SOURCE_THUMBNAIL_CAP
  )
  return {
    type: REMIX_SOURCE_DATA_PART_TYPE,
    data: {
      kind: source.kind,
      id: source.id,
      title: source.title,
      href: source.href,
      ...(source.subtitle ? { subtitle: source.subtitle } : {}),
      ...(source.imagePngUrl ? { imagePngUrl: source.imagePngUrl } : {}),
      ...(thumbnails.length > 0 ? { thumbnails } : {}),
    },
  }
}

export function isRemixSourceDataPart(
  part: unknown
): part is RemixSourceDataPart {
  if (!part || typeof part !== 'object') {
    return false
  }
  const candidate = part as { type?: unknown; data?: unknown }
  return (
    candidate.type === REMIX_SOURCE_DATA_PART_TYPE &&
    isValidRemixSourceMetadata(candidate.data)
  )
}

export const SELECTED_EMAILS_DATA_PART_TYPE = 'data-selected-emails' as const

export type SelectedEmailsDataPart = {
  type: typeof SELECTED_EMAILS_DATA_PART_TYPE
  id?: string
  data: {
    emails: Array<SelectedEmailMetadata>
  }
}

/**
 * Runtime guard: a `SelectedEmailMetadata` is only useful if `emailId` is a
 * non-empty string. Anything else round-trips through JSON badly (`undefined`
 * keys are dropped on objects and become `null` in arrays) and ends up as a
 * real JS `undefined` once read back. Convex rejects that with
 * "undefined is not a valid Convex value".
 */
function isValidSelectedEmailVersion(
  value: unknown
): value is number | 'latest' {
  if (value === 'latest') {
    return true
  }
  if (typeof value !== 'number') {
    return false
  }
  return Number.isInteger(value) && value >= 0
}

export function isValidSelectedEmailMetadata(
  value: unknown
): value is SelectedEmailMetadata {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as {
    emailId?: unknown
    title?: unknown
    emailVersionId?: unknown
    version?: unknown
  }
  if (
    typeof candidate.emailId !== 'string' ||
    candidate.emailId.length === 0 ||
    typeof candidate.title !== 'string'
  ) {
    return false
  }
  if (
    candidate.version !== undefined &&
    !isValidSelectedEmailVersion(candidate.version)
  ) {
    return false
  }
  if (
    candidate.emailVersionId !== undefined &&
    (typeof candidate.emailVersionId !== 'string' ||
      candidate.emailVersionId.length === 0)
  ) {
    return false
  }
  return true
}

export function buildSelectedEmailsDataPart(
  emails: ReadonlyArray<SelectedEmailMetadata>
): SelectedEmailsDataPart | null {
  const valid = emails.filter(isValidSelectedEmailMetadata)
  if (valid.length === 0) {
    return null
  }
  return {
    type: SELECTED_EMAILS_DATA_PART_TYPE,
    data: {
      emails: valid.map((email) => ({
        emailId: email.emailId,
        title: email.title,
        ...(email.imagePngUrl !== undefined
          ? { imagePngUrl: email.imagePngUrl }
          : {}),
        ...(typeof email.emailVersionId === 'string' &&
        email.emailVersionId.length > 0
          ? { emailVersionId: email.emailVersionId }
          : {}),
        // Only include `version` when explicitly pinned to a numeric
        // version. `undefined` and `'latest'` both round-trip as the
        // latest-version default and are dropped to keep the persisted
        // shape minimal.
        ...(typeof email.version === 'number'
          ? { version: email.version }
          : {}),
        // Owning automationId when this design is currently referenced
        // by a sendEmail node (set by the sendEmail node toolbar's
        // Add-to-chat handler). Surfaced in the orchestrator's
        // selection footer as a back-pointer for canvas/cascade
        // routing — the design itself is shared, not "owned".
        ...(typeof email.automationId === 'string' &&
        email.automationId.length > 0
          ? { automationId: email.automationId }
          : {}),
      })),
    },
  }
}

export function isSelectedEmailsDataPart(
  part: unknown
): part is SelectedEmailsDataPart {
  if (!part || typeof part !== 'object') {
    return false
  }
  const candidate = part as { type?: unknown; data?: unknown }
  if (candidate.type !== SELECTED_EMAILS_DATA_PART_TYPE) {
    return false
  }
  if (!candidate.data || typeof candidate.data !== 'object') {
    return false
  }
  const data = candidate.data as { emails?: unknown }
  return Array.isArray(data.emails)
}

/**
 * Read the latest user message's canvas selection from either the
 * `data-selected-emails` part or the legacy `metadata.selectedEmails`
 * blob. Used as a server-side fallback when the live POST body has no
 * `selectedEmailIds` (typical after `onClearSelection()` runs and the
 * user types a clarifying follow-up).
 *
 * Iterates from newest to oldest user message and returns the first hit.
 * `data-selected-emails` parts take precedence over metadata when both
 * are present on the same message.
 */
export function extractLatestUserSelectedEmails(
  messages: ReadonlyArray<{
    role?: string
    parts?: ReadonlyArray<unknown>
    metadata?: unknown
  }>
): Array<SelectedEmailMetadata> {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message?.role !== 'user') {
      continue
    }

    const parts = Array.isArray(message.parts) ? message.parts : []
    for (const part of parts) {
      if (isSelectedEmailsDataPart(part) && part.data.emails.length > 0) {
        const valid = part.data.emails.filter(isValidSelectedEmailMetadata)
        if (valid.length > 0) {
          return valid
        }
      }
    }

    if (
      typeof message.metadata === 'object' &&
      message.metadata !== null &&
      Array.isArray(
        (message.metadata as Record<string, unknown>).selectedEmails
      )
    ) {
      const fromMetadata = (message.metadata as ChatMessageMetadata)
        .selectedEmails
      if (fromMetadata && fromMetadata.length > 0) {
        const valid = fromMetadata.filter(isValidSelectedEmailMetadata)
        if (valid.length > 0) {
          return valid
        }
      }
    }
  }
  return []
}

/**
 * Type literal for the AI SDK `data-*` UI part that carries automation
 * canvas selection alongside a user message. Persisted as an ordinary
 * message part so the orchestrator can recover historical automation
 * context on follow-up turns even after the canvas auto-clears the
 * live attachment on submit.
 */
export const SELECTED_AUTOMATIONS_DATA_PART_TYPE =
  'data-selected-automations' as const

export type SelectedAutomationsDataPart = {
  type: typeof SELECTED_AUTOMATIONS_DATA_PART_TYPE
  id?: string
  data: {
    automations: Array<SelectedAutomationMetadata>
  }
}

function isValidLifecycleStatus(
  value: unknown
): value is 'draft' | 'published' {
  return value === 'draft' || value === 'published'
}

export function isValidSelectedAutomationMetadata(
  value: unknown
): value is SelectedAutomationMetadata {
  if (!value || typeof value !== 'object') {
    return false
  }
  const candidate = value as {
    automationId?: unknown
    title?: unknown
    lifecycleStatus?: unknown
  }
  if (
    typeof candidate.automationId !== 'string' ||
    candidate.automationId.length === 0 ||
    typeof candidate.title !== 'string'
  ) {
    return false
  }
  if (
    candidate.lifecycleStatus !== undefined &&
    !isValidLifecycleStatus(candidate.lifecycleStatus)
  ) {
    return false
  }
  return true
}

export function buildSelectedAutomationsDataPart(
  automations: ReadonlyArray<SelectedAutomationMetadata>
): SelectedAutomationsDataPart | null {
  const valid = automations.filter(isValidSelectedAutomationMetadata)
  if (valid.length === 0) {
    return null
  }
  return {
    type: SELECTED_AUTOMATIONS_DATA_PART_TYPE,
    data: {
      automations: valid.map((a) => ({
        automationId: a.automationId,
        title: a.title,
        ...(a.lifecycleStatus !== undefined
          ? { lifecycleStatus: a.lifecycleStatus }
          : {}),
        // Persist the exact viewed automation version id so the
        // orchestrator can resolve the right row on the next turn —
        // even if the canvas auto-cleared the live selection.
        ...(typeof a.automationVersionId === 'string' &&
        a.automationVersionId.length > 0
          ? { automationVersionId: a.automationVersionId }
          : {}),
      })),
    },
  }
}

export function isSelectedAutomationsDataPart(
  part: unknown
): part is SelectedAutomationsDataPart {
  if (!part || typeof part !== 'object') {
    return false
  }
  const candidate = part as { type?: unknown; data?: unknown }
  if (candidate.type !== SELECTED_AUTOMATIONS_DATA_PART_TYPE) {
    return false
  }
  if (!candidate.data || typeof candidate.data !== 'object') {
    return false
  }
  const data = candidate.data as { automations?: unknown }
  return Array.isArray(data.automations)
}

/**
 * Read the latest user message's automation selection from either the
 * `data-selected-automations` part or the `metadata.selectedAutomations`
 * blob — the cross-turn replay path for chats persisted under the old
 * manual "Add to chat" flow (the live composer no longer attaches
 * automations; the active one now comes from `currentUrlParams`).
 *
 * Mirrors `extractLatestUserSelectedEmails`. `data-selected-automations`
 * parts take precedence over metadata when both are present on the
 * same message.
 */
export function extractLatestUserSelectedAutomations(
  messages: ReadonlyArray<{
    role?: string
    parts?: ReadonlyArray<unknown>
    metadata?: unknown
  }>
): Array<SelectedAutomationMetadata> {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]
    if (message?.role !== 'user') {
      continue
    }

    const parts = Array.isArray(message.parts) ? message.parts : []
    for (const part of parts) {
      if (
        isSelectedAutomationsDataPart(part) &&
        part.data.automations.length > 0
      ) {
        const valid = part.data.automations.filter(
          isValidSelectedAutomationMetadata
        )
        if (valid.length > 0) {
          return valid
        }
      }
    }

    if (
      typeof message.metadata === 'object' &&
      message.metadata !== null &&
      Array.isArray(
        (message.metadata as Record<string, unknown>).selectedAutomations
      )
    ) {
      const fromMetadata = (message.metadata as ChatMessageMetadata)
        .selectedAutomations
      if (fromMetadata && fromMetadata.length > 0) {
        const valid = fromMetadata.filter(isValidSelectedAutomationMetadata)
        if (valid.length > 0) {
          return valid
        }
      }
    }
  }
  return []
}

export type ChatParticipant = {
  userId: string
  displayName: string
  initials: string
  imageUrl?: string
}

type ParticipantNameInput = {
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  emailAddress?: string | null
  userId?: string
}

const USER_AVATAR_GRADIENTS = [
  ['#fb923c', '#f97316', '#ea580c'],
  ['#f59e0b', '#f97316', '#dc2626'],
  ['#a855f7', '#7c3aed', '#4f46e5'],
  ['#22c55e', '#14b8a6', '#0f766e'],
  ['#06b6d4', '#2563eb', '#1d4ed8'],
  ['#ec4899', '#db2777', '#9d174d'],
  ['#84cc16', '#65a30d', '#3f6212'],
  ['#f43f5e', '#f97316', '#ea580c'],
] as const

export function getMessageUserId(
  message: Pick<UIMessage, 'role'> & { metadata?: unknown }
): string | undefined {
  if (message.role !== 'user') {
    return
  }
  const metadata =
    typeof message.metadata === 'object' && message.metadata !== null
      ? (message.metadata as ChatMessageMetadata)
      : undefined
  const userId = metadata?.userId?.trim()
  return userId ? userId : undefined
}

/**
 * Stamp `userId` onto the user-message metadata. Selection
 * (`selectedEmails` / `selectedAutomations`) is NO LONGER written
 * here — it lives canonically in the `data-selected-*` UIDataParts on
 * the message (`buildSelected*DataPart`). Both the chat UI chip
 * renderer and the orchestrator model conversion read from those
 * parts; the `metadata.selected*` fields on `ChatMessageMetadata` are
 * retained as a back-compat read fallback for already-persisted
 * legacy rows only.
 */
export function withUserMessageMetadata<T extends UIMessage>(
  message: T,
  userId?: string | null
): WithChatMessageMetadata<T> {
  if (message.role !== 'user') {
    return message as WithChatMessageMetadata<T>
  }

  const normalizedUserId = userId?.trim()
  if (!normalizedUserId) {
    return message as WithChatMessageMetadata<T>
  }

  const base =
    typeof message.metadata === 'object' && message.metadata !== null
      ? (message.metadata as Record<string, unknown>)
      : {}

  return {
    ...message,
    metadata: {
      ...base,
      userId: normalizedUserId,
    },
  } as WithChatMessageMetadata<T>
}

export function extractParticipantUserIds(
  messages: Array<
    Pick<WithChatMessageMetadata, 'role' | 'metadata'> & {
      parts?: ReadonlyArray<unknown>
    }
  >
): Array<string> {
  const uniqueUserIds = new Set<string>()

  for (const message of messages) {
    const userId = getMessageUserId(message)
    if (userId) {
      uniqueUserIds.add(userId)
    }
    if (message.role === 'user') {
      const source = message.metadata?.channelSource
      if (isChannelMessageSource(source) && source.userId) {
        uniqueUserIds.add(source.userId)
      }
      for (const author of getChannelThreadMessages(message.parts ?? [])) {
        if (author.userId) {
          uniqueUserIds.add(author.userId)
        }
      }
    }
  }

  return [...uniqueUserIds]
}

export function getParticipantDisplayName({
  firstName,
  lastName,
  fullName,
  emailAddress,
  userId,
}: ParticipantNameInput): string {
  if (fullName?.trim()) {
    return fullName.trim()
  }

  const combinedName = [firstName, lastName]
    .filter(
      (value): value is string =>
        typeof value === 'string' && value.trim().length > 0
    )
    .join(' ')
    .trim()

  if (combinedName) {
    return combinedName
  }
  if (emailAddress?.trim()) {
    return emailAddress.trim()
  }
  if (userId?.trim()) {
    return userId.trim()
  }
  return 'Unknown user'
}

export function getParticipantInitials({
  firstName,
  lastName,
  fullName,
  emailAddress,
  userId,
}: ParticipantNameInput): string {
  const firstInitial = firstName?.trim()?.[0]
  const lastInitial = lastName?.trim()?.[0]

  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const source =
    fullName?.trim() || emailAddress?.trim() || userId?.trim() || 'User'

  const tokens = source
    .split(/[\s._-]+/)
    .map((token) => token.trim())
    .filter(Boolean)

  if (tokens.length >= 2) {
    return `${tokens[0][0]}${tokens[1][0]}`.toUpperCase()
  }

  return (
    source
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase() || 'U'
  )
}

export function getUserAvatarGradient(userId?: string): string {
  const normalizedUserId = userId?.trim() || 'user'
  let hash = 0

  for (let index = 0; index < normalizedUserId.length; index += 1) {
    hash = (hash * 31 + normalizedUserId.charCodeAt(index)) >>> 0
  }

  const [highlight, base, edge] =
    USER_AVATAR_GRADIENTS[hash % USER_AVATAR_GRADIENTS.length]

  return `radial-gradient(circle at 28% 22%, ${highlight} 0%, ${base} 52%, ${edge} 100%)`
}
